import React, { useMemo } from "react";
import { getStyles } from "../../../assets/js/utils/functions";
import { computeItemAvailability, getSizedImage } from "../../../assets/js/utils/utils";
import useScreenSize, { BreakPoint } from "../../hooks/useScreenSize";
import useSlider from "../../hooks/useSlider";
import ProductItem from "./product-item";
import ProductItemLoader from "./product-item-loader";
import { ProductItemInterface } from "@/assets/interfaces";
import { EmptyItems } from "../ui";

interface Props {
  productsLoading: boolean;
  items: ProductItemInterface[];
  setSelectedItem: (item: ProductItemInterface) => void;
  classNames?: string;
  noPadding?: boolean;
}

const RelatedProducts: React.FC<Props> = ({
  productsLoading,
  items,
  setSelectedItem,
  classNames,
  noPadding = false,
}) => {
  const { screen } = useScreenSize();
  const { next, slider, previous } = useSlider({
    slides: items?.length || 0,
    sameWidthAsSlider: false,
    gap: 24,
  });

  const formattedItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    return items.map((i) => computeItemAvailability(i));
  }, [items]);

  if (!productsLoading && (!items || items.length === 0)) {
    return null;
  }

  // Render the loading state
  if (productsLoading) {
    return (
      <section className={`py-3 mb-7 ${classNames}`}>
        <div className={`w-full block ${noPadding ? "px-0" : "px-5 sm:px-9 md:px-12.5"}`}>
          <div className="store-container space-y-3 sm:space-y-5 md:space-y-5">
            <div className="container flex justify-between items-center mb-5 sm:mb-5.5 lg:mb-6.5">
              <h3 className="text-black text-1sm sm:text-lg md:text-lg font-bold">You may also like</h3>
            </div>
            <ul className="grid grid-cols-[repeat(5,48%)] sm:grid-cols-[repeat(5, 42%)] md:grid-cols-[repeat(5, 32%)] lg:grid-cols-[repeat(5,24%)] gap-6 md:gap-8">
              {Array.from({ length: 5 }).map((_, index) => (
                <ProductItemLoader key={index} />
              ))}
            </ul>
          </div>
        </div>
      </section>
    );
  }

  // Render the empty state
  if (!productsLoading && (!items || items.length === 0)) {
    return (
      <section className={`py-3 mb-7 ${classNames}`}>
        <div className={`w-full block  ${noPadding ? "px-0" : "px-5 sm:px-9 md:px-12.5"}`}>
          <div className="store-container space-y-3 sm:space-y-5 md:space-y-5">
            <div className="col-span-full">
              <EmptyItems />
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Render the related products
  return (
    <section className={`py-3 mb-7 ${classNames}`}>
      <div className="w-full block">
        <div className="space-y-3 sm:space-y-5 md:space-y-5">
          <div className={`${noPadding ? "px-0" : "px-5 sm:px-9 md:px-12.5"}`}>
            <div className="store-container">
              <div className="container flex justify-between items-center mb-5 sm:mb-5.5 lg:mb-6.5">
                <h3 className="text-black text-1sm sm:text-lg md:text-lg font-bold">You may also like</h3>
                <div className="hidden md:flex items-center space-x-3">
                  <button
                    className="h-6.25 sm:h-7.5 w-8 sm:w-10 rounded-15 flex items-center justify-center bg-grey-fields-100 text-primary-500"
                    onClick={previous}
                    aria-label="previous"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M13 7H1"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M7 1L1 7L7 13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    className="h-6.25 sm:h-7.5 w-8 sm:w-10 rounded-15 text-white flex items-center justify-center bg-grey-fields-100 text-primary-500"
                    onClick={next}
                    aria-label="next"
                  >
                    <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M6 0L4.9425 1.0575L9.1275 5.25H0V6.75H9.1275L4.9425 10.9425L6 12L12 6L6 0Z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <ul
            className={`flex items-start space-x-6 overflow-x-auto scrollbar-hide ${
              !noPadding && "carousel-responsive-padding"
            }`}
            ref={slider}
          >
            {formattedItems.map((item, index) => (
              <div className="min-w-[250px] sm:min-w-[300px]">
                <ProductItem
                  index={index}
                  key={item.id}
                  item={item}
                  showAddToCart={false}
                  openItem={() => setSelectedItem(item)}
                />
              </div>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default RelatedProducts;
